import React from "react";
import { Modal, Stack, Avatar, Text, Title, Flex } from "@mantine/core";
import zIndex from "../../designTokens/zIndex";
import { MessageContainer } from "../Container/MessageContainer";
import { useIsMobile } from "../../hooks/useIsMobile";
import moment from "moment";

export const FriendConversationModal = ({
  opened,
  onClose,
  selectedUser,
  selectedThreads,
}) => {
  const isMobile = useIsMobile("xl");

  // 첫 번째 문장 추출 함수
  const getFirstSentence = (text) => {
    if (!text) return "";
    // 개행, 마침표, 물음표를 기준으로 첫 번째 문장 추출
    const sentences = text
      .split(/[\n.?!]/)
      .filter((s) => s.trim().length > 0);
    return sentences.length > 0 ? sentences[0].trim() : text;
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`${selectedUser}님과의 대화`}
      size="xl"
      zIndex={zIndex.modal}
      styles={{
        modal: {
          maxWidth: "1200px",
          maxHeight: "90vh",
        },
        header: {
          paddingLeft: isMobile ? "20px" : "32px",
          paddingRight: isMobile ? "20px" : "32px",
        },
        title: {
          fontSize: isMobile ? "1.25rem" : "1.75rem", // 모바일: xl(20px), PC: 2xl(28px)
          fontWeight: 900,
        },
        body: {
          paddingLeft: isMobile ? "20px" : "32px",
          paddingRight: isMobile ? "20px" : "32px",
          paddingBottom: isMobile ? "20px" : "32px",
        },
      }}
    >
      <div
        style={{
          maxHeight: "70vh",
          overflowY: "auto",
          width: "100%",
        }}
      >
        {selectedThreads && selectedThreads.length > 0 ? (
          selectedThreads.map((threadGroup, groupIndex) => {
            const isLastItem = groupIndex === selectedThreads.length - 1;

            return (
              <div
                key={groupIndex}
                style={{
                  width: "100%",
                  marginBottom: isLastItem ? "0" : "40px",
                  paddingBottom: isLastItem ? "0" : "32px",
                  borderBottom: isLastItem ? "none" : "1px solid #e9ecef",
                  position: "relative",
                  paddingLeft: "56px", // 타임라인 마크를 위한 더 넉넉한 공간
                }}
              >
                {/* 타임라인 원 마크 */}
                <div
                  style={{
                    position: "absolute",
                    left: "8px", // 더 안전한 위치로 이동
                    top: "12px",
                    width: "16px",
                    height: "16px",
                    backgroundColor: "#000000",
                    borderRadius: "50%",
                    border: "3px solid white",
                    boxShadow: "0 0 0 2px #000000",
                    zIndex: 2,
                  }}
                />

                {/* 타임라인 연결선 */}
                {!isLastItem && (
                  <div
                    style={{
                      position: "absolute",
                      left: "15px", // 원 마크의 중앙에 맞춤 (8px + 8px - 1px)
                      top: "28px",
                      width: "2px",
                      height: "calc(100% - 16px + 40px)",
                      backgroundColor: "#e9ecef",
                      zIndex: 1,
                    }}
                  />
                )}

                {/* 원글 제목 - 크게 표시하고 첫 번째 문장만 */}
                <Title
                  order={isMobile ? 4 : 3}
                  c="dark"
                  fw={600}
                  mb="8px"
                  style={{
                    lineHeight: 1.4,
                    wordBreak: "break-word",
                  }}
                >
                  {getFirstSentence(
                    threadGroup.threadText?.replace("...", "")
                  )}
                </Title>

                {/* 원글 날짜 */}
                <Text
                  size={isMobile ? "sm" : "md"}
                  c="gray"
                  mb="20px"
                  style={{
                    fontWeight: 500,
                  }}
                >
                  {threadGroup.conversations && threadGroup.conversations.length > 0 && threadGroup.conversations[0].timestamp
                    ? moment(threadGroup.conversations[0].timestamp).format("YYYY년 MM월 DD일 HH:mm")
                    : threadGroup.timestamp
                    ? moment(threadGroup.timestamp).format("YYYY년 MM월 DD일 HH:mm")
                    : "날짜 정보 없음"}
                </Text>

                {/* 해당 원글의 대화들 */}
                <div style={{ width: "100%" }}>
                  <Stack gap="16px">
                    {threadGroup.conversations.map((conversation, index) => {
                      const isMyComment = conversation.type === "my_reply";

                      return (
                        <Flex
                          key={index}
                          direction="row"
                          align="flex-start"
                          gap="12px"
                          justify={isMyComment ? "flex-end" : "flex-start"}
                          style={{
                            width: "100%",
                            padding: "8px 0",
                          }}
                        >
                          {/* 상대방 댓글일 때만 왼쪽 아바타 */}
                          {!isMyComment && (
                            <Avatar size={40} color="dark" radius="xl">
                              {selectedUser?.substring(0, 2).toUpperCase()}
                            </Avatar>
                          )}

                          {/* 메시지 컨테이너 */}
                          <Stack
                            gap="8px"
                            style={{
                              flex: 1,
                              maxWidth: "80%",
                              alignItems: isMyComment
                                ? "flex-end"
                                : "flex-start",
                            }}
                          >
                            {/* 메시지 내용 */}
                            <a
                              href={conversation?.comment?.permalink || "#"}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                textDecoration: "none",
                                color: "inherit",
                              }}
                            >
                              <MessageContainer
                                p="16px 20px"
                                rd={20}
                                bg={isMyComment ? "#f8f9fa" : "white"}
                                style={{
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                }}
                              >
                                <Stack gap="8px">
                                  <Text
                                    size={isMobile ? "md" : "lg"}
                                    c={isMyComment ? "dark" : "inherit"}
                                    style={{
                                      lineHeight: 1.5,
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {conversation?.comment?.text ||
                                      "내용이 없습니다."}
                                  </Text>

                                  <Text
                                    size={isMobile ? "xs" : "sm"}
                                    c="gray"
                                    style={{
                                      textAlign: "right",
                                      marginTop: "4px",
                                    }}
                                  >
                                    {conversation?.timestamp
                                      ? moment(conversation.timestamp).format(
                                          "MM/DD HH:mm"
                                        )
                                      : "날짜 정보 없음"}
                                  </Text>
                                </Stack>
                              </MessageContainer>
                            </a>
                          </Stack>

                          {/* 내 댓글일 때만 오른쪽 아바타 - 옅은회색 배경, 검정 전경 */}
                          {isMyComment && (
                            <Avatar
                              size={40}
                              radius="xl"
                              styles={{
                                root: {
                                  backgroundColor: "#f1f3f4 !important",
                                },
                                placeholder: {
                                  backgroundColor: "#f1f3f4 !important",
                                  color: "#000000 !important",
                                },
                              }}
                            >
                              ME
                            </Avatar>
                          )}
                        </Flex>
                      );
                    })}
                  </Stack>
                </div>
              </div>
            );
          })
        ) : (
          <Text ta="center" c="dimmed" size={isMobile ? "md" : "lg"}>
            댓글이 없습니다.
          </Text>
        )}
      </div>
    </Modal>
  );
}; 